import { Component, HostBinding, Inject, LOCALE_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputComponent } from '../../shared/input/input.component';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { PasswordRequirementsComponent } from '../../shared/password-requirements/password-requirements.component';
import { passwordRequirementsValidator } from '../../utilities/passwordRequirements';
import { directionForLocale } from '../../utilities/localeDirection';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    ToastModule,
    InputComponent,
    PasswordRequirementsComponent,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent {
  forgotPasswordForm: FormGroup;
  resetCodeForm: FormGroup;
  step: 'email' | 'reset-code' = 'email';
  isLoading = false;
  @HostBinding('attr.dir') get direction(): 'rtl' | 'ltr' {
    return directionForLocale(this.locale);
  }

  get newPassword(): string {
    return this.resetCodeForm.get('newPassword')?.value ?? '';
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private messageService: MessageService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.forgotPasswordForm = this.fb.group({
      username: [null, [Validators.required]],
    });

    this.resetCodeForm = this.fb.group({
      confirmationCode: [null, [Validators.required]],
      newPassword: [null, [Validators.required, passwordRequirementsValidator()]],
      confirmPassword: [null, [Validators.required]],
    });
  }

  async sendResetCode(): Promise<void> {
    if (this.forgotPasswordForm.invalid) {
      return;
    }

    this.isLoading = true;
    try {
      const username = this.forgotPasswordForm.value.username;
      await resetPassword({ username });

      this.messageService.add({
        severity: 'success',
        summary: $localize`:@@forgotPassword.codeSentSummary:Code Sent`,
        detail: $localize`:@@forgotPassword.codeSentDetail:Check your email for the password reset code.`,
        closable: true,
        icon: 'ri-check-line',
      });

      this.step = 'reset-code';
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@forgotPassword.resetFailedSummary:Reset Failed`,
        detail: error?.message || $localize`:@@forgotPassword.resetFailedDetail:Could not send reset code. Please try again.`,
        closable: true,
        icon: 'ri-error-warning-fill',
      });
    } finally {
      this.isLoading = false;
    }
  }

  async submitNewPassword(): Promise<void> {
    const { confirmationCode, newPassword, confirmPassword } =
      this.resetCodeForm.value;

    if (!confirmationCode || !newPassword || !confirmPassword) {
      return;
    }

    if (newPassword !== confirmPassword) {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@newPassword.mismatchSummary:Password Mismatch`,
        detail: $localize`:@@newPassword.mismatchDetail:Password and confirm password must match.`,
        closable: true,
        icon: 'ri-error-warning-fill',
      });
      return;
    }

    this.isLoading = true;
    try {
      const username = this.forgotPasswordForm.value.username;
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });

      this.messageService.add({
        severity: 'success',
        summary: $localize`:@@forgotPassword.passwordUpdatedSummary:Password Updated`,
        detail: $localize`:@@forgotPassword.passwordUpdatedDetail:Your password has been reset successfully.`,
        closable: true,
        icon: 'ri-check-line',
      });

      setTimeout(() => {
        this.router.navigate(['/login']);
      }, 1500);
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: $localize`:@@forgotPassword.resetFailedSummary:Reset Failed`,
        detail: error?.message || $localize`:@@forgotPassword.resetFailedDetail2:Could not reset password. Please try again.`,
        closable: true,
        icon: 'ri-error-warning-fill',
      });
    } finally {
      this.isLoading = false;
    }
  }

  goBack(): void {
    if (this.step === 'reset-code') {
      this.step = 'email';
      this.resetCodeForm.reset();
    } else {
      this.router.navigate(['/login']);
    }
  }
}
